import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@reflex/ui/lib/utils';
import type { OrgOnboardingProfile } from '@reflex/shared';

/**
 * The one segmentation question both create-org surfaces ask. We
 * deliberately do NOT ask users to pick plugins — we don't yet know our
 * customers well enough to make plugin-level questions meaningful, so they
 * describe the work they plan to do and Reflex derives an educated plugin selection from
 * it server-side (`computeBootstrapSetForProfile`). Plugins can be reviewed
 * and changed later from Admin -> Plugins.
 */
const ORG_ONBOARDING_PROFILE_OPTIONS: ReadonlyArray<{
  value: OrgOnboardingProfile;
  title: string;
  description: string;
}> = [
  {
    value: 'engineering',
    title: 'Software engineering',
    description:
      'Build and ship software with coding agents, sandboxes, code review, and GitHub tooling.',
  },
  {
    value: 'general',
    title: 'Everything else',
    description: 'Use chat, automations, and cost tracking without software-development tooling.',
  },
];

interface OrgOnboardingProfileSelectorProps {
  /** The selected profile, or `undefined` when nothing is chosen yet. */
  value: OrgOnboardingProfile | undefined;
  onChange: (profile: OrgOnboardingProfile) => void;
  /** Id of the label element wired to the radiogroup for accessibility. */
  labelId: string;
  required?: boolean;
  /**
   * Prefix for each card's `data-testid` (`<prefix>-<profile>`). Defaults to
   * the wizard's convention so its existing stories keep matching.
   */
  testIdPrefix?: string;
  className?: string;
}

/**
 * Controlled two-card radio group for the self-described onboarding profile.
 * Shared by the self-service setup wizard (`OrgSetupPage`) and the admin
 * create-organization dialog so both derive plugins the same way.
 */
export function OrgOnboardingProfileSelector({
  value,
  onChange,
  labelId,
  required = false,
  testIdPrefix = 'org-setup-kind',
  className,
}: OrgOnboardingProfileSelectorProps) {
  const radioRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // ARIA radiogroup keyboard contract: arrows move the selection (and DOM
  // focus) between radios, wrapping at the ends, so keyboard-only users can
  // navigate without a pointer. Left/Up step to the previous option,
  // Right/Down to the next.
  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % ORG_ONBOARDING_PROFILE_OPTIONS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex =
          (index - 1 + ORG_ONBOARDING_PROFILE_OPTIONS.length) %
          ORG_ONBOARDING_PROFILE_OPTIONS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = ORG_ONBOARDING_PROFILE_OPTIONS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    onChange(ORG_ONBOARDING_PROFILE_OPTIONS[nextIndex].value);
    radioRefs.current[nextIndex]?.focus();
  }

  // Roving tabindex: the checked radio (or the first, when nothing is checked
  // yet) is the single Tab stop; arrows move focus within the group.
  const focusableIndex = Math.max(
    0,
    ORG_ONBOARDING_PROFILE_OPTIONS.findIndex((option) => option.value === value),
  );

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelId}
      aria-required={required}
      className={cn('grid gap-3 sm:grid-cols-2', className)}
    >
      {ORG_ONBOARDING_PROFILE_OPTIONS.map((option, index) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              radioRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === focusableIndex ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={`${testIdPrefix}-${option.value}`}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border bg-card hover:border-muted-foreground/40',
            )}
          >
            <span className="block text-sm font-medium text-foreground">{option.title}</span>
            <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
              {option.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}
